/**
 * Pillar Challenges — multi-day discipline tracker anchored to the
 * SOCOM / SOF 3-pillar framework (Physical · Mental · Spiritual).
 *
 * Doctrinal basis:
 *   - THOR3 (Tactical Human Optimization, Special Forces) — three
 *     domains of operator performance: physical, cognitive (mental),
 *     spiritual readiness.
 *   - US Army Spiritual Readiness doctrine: "the personal qualities
 *     a soldier needs to sustain themselves in times of stress." NOT
 *     religion-required — can be religious, philosophical, or values-
 *     based.
 *   - POTFF (Preservation of the Force and Family).
 *
 * Why this lives outside lib/sessionStorage:
 *   Challenges aren't sessions. They're commitment frameworks that
 *   READ from many sources (sessions, nutrition, activity, manual
 *   check-ins) and roll up into a daily status. The whole point is
 *   to integrate everything the user already has data for and
 *   surface gaps the app can't auto-detect.
 *
 * The auto-detection is the OPMODE advantage: every other 75-Hard-
 * style tracker is manual all the way down. We already capture
 * workout duration, calorie totals, steps, sleep, HRV — so 70% of
 * commitments self-check.
 */

import type { Timestamp } from 'firebase/firestore';

// ============================================================================
// CORE TYPES
// ============================================================================

/** The three pillars. Order matters — UI renders in this sequence. */
export type Pillar = 'physical' | 'mental' | 'spiritual';

export const PILLARS: Pillar[] = ['physical', 'mental', 'spiritual'];

export const PILLAR_LABELS: Record<Pillar, string> = {
  physical: 'Physical',
  mental: 'Mental',
  spiritual: 'Spiritual',
};

/**
 * Why-statement: short personal motivation surfaced at decision
 * points. User writes it during setup; the app rotates it through the
 * day. Behavioral science: implementation intentions + identity-based
 * framing > pure goal-setting (Gollwitzer, Clear).
 */
export interface WhyStatement {
  text: string;            // 1-2 sentences
  /** When to display: rotate at top of card, surface at decision points,
   *  or silent (data captured but not shown). */
  displayMode: 'rotate' | 'decision-only' | 'silent';
}

/**
 * Failure mode — how the engine decides when a day has failed.
 *
 *   strict: any commitment missed → day fails (classic 75 Hard).
 *           Best for short challenges + purists.
 *   pillar-collapse: a pillar fails only when ALL its commitments
 *           fail. Day passes if all 3 pillars have at least one ✓.
 *           Best for longer challenges + sustainability.
 *
 * Default per preset; user can override at setup.
 */
export type FailureMode = 'strict' | 'pillar-collapse';

/**
 * How an individual commitment gets checked. Auto types pull from
 * existing OPMODE data; manual is a single tap.
 */
export type AutoDetectKind =
  | 'manual-tap'                  // Bible read, gratitude, etc. — one-tap card
  | 'manual-confirm'              // No alcohol — slide-to-confirm to avoid accidental tap
  | 'session-duration'            // sum of today's session durations >= threshold
  | 'session-type-match'          // a session matching name regex (yoga, ruck, etc.)
  | 'calorie-under-goal'          // today's nutrition sum < goal
  | 'water-quantity'              // manual quick-tap counter, threshold in oz
  | 'steps-goal'                  // dailyActivity.steps >= threshold
  | 'sleep-minutes-goal'          // dailyActivity.sleepDurationMinutes >= threshold
  | 'wake-by-time'                // first app foreground event before HH:MM
  | 'weight-logged'               // any weight entry today
  | 'time-outdoors'               // manual minute tracker
  | 'screen-time-cutoff'          // manual: did you stop screens at threshold-time
  | 'evidence-journal';           // today's evidence.journal has non-empty text

/**
 * One commitment a user can pick from. The library is curated — quality
 * over quantity — so the picker doesn't overwhelm. Future: user-
 * submitted commitments would go through review.
 */
export interface Commitment {
  /** Stable id used in challenge configs + persistence. */
  id: string;
  pillar: Pillar;
  label: string;                  // "Workout 45+ min", "Read scripture"
  /** Short description for the picker UI. */
  description: string;
  autoDetect: AutoDetectKind;
  /**
   * Parameters for the detector. Shape depends on autoDetect. Examples:
   *   { minutes: 45 } for session-duration
   *   { regex: 'yoga|flow' } for session-type-match
   *   { time: '06:30' } for wake-by-time
   *   { minutes: 420 } for sleep-minutes-goal
   *   {} for manual-tap
   */
  config: Record<string, unknown>;
  /** True for items that can be customized at setup (e.g. wake time). */
  configurable?: boolean;
  /** Optional hint shown next to the commitment in the daily card. */
  hint?: string;
  /** Tags for filtering presets — e.g. 'sobriety', 'sleep', 'core-discipline'. */
  tags?: string[];
}

/**
 * A user's active or completed challenge. Lives in
 * users/{uid}/challenges/{challengeId} in Firestore.
 */
export interface Challenge {
  id: string;
  userId: string;
  /** Preset this challenge was created from. Null for fully custom. */
  presetId: string | null;
  /** Display name — preset name or user-provided. */
  name: string;
  /** Length in days. */
  duration: number;
  /** ISO start date (local). */
  startDate: string;
  /** Selected commitment IDs grouped by pillar. */
  commitments: {
    physical: string[];
    mental: string[];
    spiritual: string[];
  };
  /** Per-commitment overrides — e.g. wake time, calorie goal. Keyed by commitment id. */
  configOverrides: Record<string, Record<string, unknown>>;
  failureMode: FailureMode;
  /** Grace days allowed. 0 = strict 75 Hard. */
  graceDays: number;
  /** Grace days used so far this attempt. */
  graceDaysUsed: number;
  /** Mid-challenge commitment swaps used. Bounded by MAX_SWAPS — at the
   *  ceiling, the swap UI disables to prevent the structure becoming a
   *  shopping list. Optional in storage for backwards compat (legacy
   *  challenges read as 0). */
  swapsUsed?: number;
  /** Why-statement (optional but recommended). */
  why: WhyStatement | null;
  /** Notification preferences. */
  notifications: {
    morningBriefing: boolean;     // default 6:25 AM push
    /** Default 5:15 PM nudge if items open — early enough to act on.
     *  Optional in storage so legacy challenges (created before this
     *  field existed) treat `undefined` as opted-in via runtime default. */
    earlyEvening?: boolean;
    eveningWarning: boolean;      // default 9 PM nudge if items open
    finalCall: boolean;           // default 11:30 PM last warning
  };
  /** Custom times for each notification (optional). When absent, the runtime
   *  defaults apply — DEFAULT_NOTIFICATION_TIMES in
   *  lib/pillarChallengeNotifications.ts is the single source of truth; do not
   *  restate the values here, that is what drifted last time. Stored as 24h
   *  "HH:MM" strings so they round-trip safely through Firestore. */
  notificationTimes?: {
    morningBriefing?: string;     // "HH:MM" 24h
    earlyEvening?: string;
    eveningWarning?: string;
    finalCall?: string;
  };
  /**
   * Rollover watermark — the LAST local date (YYYY-MM-DD) whose outcome
   * has been folded into the counters below. Everything at or before it
   * is settled history and is NEVER re-evaluated.
   *
   * Optional because every challenge that existed before catch-up
   * rollover shipped has no value here. `catchUpRollover` SEEDS a missing
   * value to YESTERDAY rather than to `startDate` — see the comment on
   * that function. This is deliberate and load-bearing: seeding to
   * startDate would re-litigate (and mass-fail) every day a user on day
   * 70 has already lived.
   */
  lastRolloverDate?: string;
  /** Current state. */
  status: 'active' | 'completed' | 'failed' | 'paused';
  /** Attempt number (incremented on hard restart). */
  attempt: number;
  /** Total days completed across all attempts for this challenge name. */
  totalDaysCompleted: number;
  /** Current streak in this attempt. */
  currentStreak: number;
  /** Personal-best streak across all attempts. */
  longestStreak: number;
  createdAt: string | Timestamp;
  updatedAt: string | Timestamp;
  completedAt?: string | Timestamp;
}

/**
 * Per-day status. Lives in
 * users/{uid}/challenges/{challengeId}/days/{YYYY-MM-DD}.
 */
export interface DailyStatus {
  /** YYYY-MM-DD local date. Doc id. */
  date: string;
  /** Day number within this attempt (1..duration). */
  dayNumber: number;
  /** Commitment IDs completed today, with timestamps for analytics.
   *  Optional `note` carries the user's quick-entry text for
   *  reflection-type commitments (gratitude list, prayer text,
   *  values check) — the entry serves as both the act AND the
   *  artifact, so we keep it for later review. */
  completed: Record<string, { at: string; auto: boolean; note?: string }>;
  /** Per-pillar tally (computed, denormalized for fast queries). */
  pillarCounts: {
    physical: { done: number; total: number };
    mental: { done: number; total: number };
    spiritual: { done: number; total: number };
  };
  /** Day outcome — computed at midnight rollover or on demand. */
  outcome: 'pending' | 'passed' | 'failed' | 'grace-used';
  /** Daily proof-of-life capture. Original 75 Hard makes a progress
   *  photo mandatory — visual documentation per day is the load-bearing
   *  accountability mechanism that pushes completion rate up. We expose
   *  it here as optional (users can complete without) but the UI
   *  surfaces it prominently so the path of least resistance is to
   *  capture both. */
  evidence?: {
    /** Public download URL (Firebase Storage). Absent until upload. */
    photoUrl?: string;
    /** Storage path used for cleanup on delete. */
    photoPath?: string;
    /** One-line journal (max ~280 chars in UI). Captures what shifted
     *  today — surfaces in the day-breakdown modal weeks later as
     *  "this is why you didn't quit on Day 47." */
    journal?: string;
    capturedAt?: string;
  };
  /** Updated at last evaluation (midnight, manual check-in, etc.) */
  updatedAt: string | Timestamp;
}

// ============================================================================
// COMMITMENT LIBRARY — curated, inclusive, doctrinally anchored
// ============================================================================

/** Maximum number of mid-challenge swaps allowed per attempt. Higher
 *  numbers let users shop their way out of friction; lower numbers
 *  feel punitive when life genuinely changes. Two is the published
 *  goldilocks for 75-day challenges. */
export const MAX_SWAPS = 2;

export const COMMITMENT_LIBRARY: Commitment[] = [
  // ── PHYSICAL ─────────────────────────────────────────────────────────
  {
    id: 'workout-45min',
    pillar: 'physical',
    label: 'Workout 45+ min',
    description: 'Any session (strength, conditioning, sport) lasting 45 minutes or more.',
    autoDetect: 'session-duration',
    config: { minutes: 45 },
    configurable: true,
    hint: 'Auto-checked when a session completes',
    tags: ['core-discipline', 'training'],
  },
  {
    id: 'workout-2x45min',
    pillar: 'physical',
    label: '2× 45-min workouts (one outdoors)',
    description: 'Classic 75 Hard rule. Two separate sessions of 45+ min, one of them outdoors.',
    autoDetect: 'session-duration',
    config: { minutes: 45, count: 2, outdoorRequired: true },
    configurable: true,
    hint: 'Auto-checks workout count; outdoor flag is manual',
    tags: ['75-hard-classic', 'training'],
  },
  {
    id: 'yoga-sunday',
    pillar: 'physical',
    label: 'Sunday yoga / mobility',
    description: 'Yoga, mobility, or flow session on Sundays (replaces a regular workout).',
    autoDetect: 'session-type-match',
    config: { regex: 'yoga|mobility|stretch|flow', minutes: 30, dayOfWeek: 0 },
    configurable: true,
    hint: 'Sunday only — auto-checked from session name',
    tags: ['recovery'],
  },
  {
    id: 'calorie-under-goal',
    pillar: 'physical',
    label: 'Stay under calorie goal',
    description: 'Daily nutrition total stays below your set calorie goal.',
    autoDetect: 'calorie-under-goal',
    config: { useProfileGoal: true },
    configurable: true,
    hint: 'Auto-evaluates from fuel page at end of day',
    tags: ['nutrition', 'core-discipline'],
  },
  {
    id: 'water-gallon',
    pillar: 'physical',
    label: 'Drink 1 gallon water',
    description: 'Hit 128 oz of water in a day. Quick-tap counter.',
    autoDetect: 'water-quantity',
    config: { ounces: 128 },
    configurable: true,
    hint: 'Tap to log each cup/bottle',
    tags: ['75-hard-classic', 'hydration'],
  },
  {
    id: 'steps-10k',
    pillar: 'physical',
    label: '10,000 steps',
    description: 'Daily step count from your watch / phone.',
    autoDetect: 'steps-goal',
    config: { steps: 10000 },
    configurable: true,
    hint: 'Auto-checked from Health Connect / Apple Health',
    tags: ['neat'],
  },
  {
    id: 'sleep-7hr',
    pillar: 'physical',
    label: '7+ hours sleep',
    description: 'Sleep duration recorded by your watch hits 7 hours.',
    autoDetect: 'sleep-minutes-goal',
    config: { minutes: 420 },
    configurable: true,
    hint: 'Auto-checked from wearable',
    tags: ['recovery'],
  },
  {
    id: 'weigh-in-daily',
    pillar: 'physical',
    label: 'Daily weigh-in',
    description: 'Step on the scale once per day. Weight enters body comp tracking.',
    autoDetect: 'weight-logged',
    config: {},
    hint: 'Manual or auto from smart scale',
    tags: ['body-comp'],
  },

  // ── MENTAL ───────────────────────────────────────────────────────────
  {
    id: 'wake-by-time',
    pillar: 'mental',
    label: 'Wake by 6:30 AM',
    description: 'First app open of the day is before your wake threshold.',
    autoDetect: 'wake-by-time',
    config: { time: '06:30' },
    configurable: true,
    hint: 'Auto-checked from when you first open the app',
    tags: ['discipline', 'core-discipline'],
  },
  {
    id: 'no-alcohol',
    pillar: 'mental',
    label: 'No alcohol',
    description: 'Zero alcoholic drinks today. End-of-day slide-to-confirm.',
    autoDetect: 'manual-confirm',
    config: {},
    hint: 'Slide to confirm at end of day',
    tags: ['sobriety', 'core-discipline'],
  },
  {
    id: 'reading-10pages',
    pillar: 'mental',
    label: 'Read 10 pages of non-fiction',
    description: 'Scripture, self-help, biography, or technical reading. 10+ pages.',
    autoDetect: 'manual-tap',
    config: { pages: 10 },
    configurable: true,
    hint: 'Tap to mark complete',
    tags: ['75-hard-classic', 'growth'],
  },
  {
    id: 'journal',
    pillar: 'mental',
    label: 'Journal entry',
    description: 'Write a journal entry today. Any length.',
    autoDetect: 'evidence-journal',
    config: {},
    hint: 'Auto-checks when you fill the evidence journal',
    tags: ['reflection'],
  },
  {
    id: 'meditate-10min',
    pillar: 'mental',
    label: '10 min meditation',
    description: 'Focused breathing, mindfulness, or guided meditation.',
    autoDetect: 'manual-tap',
    config: { minutes: 10 },
    configurable: true,
    hint: 'Manual tap',
    tags: ['emotional-regulation'],
  },
  {
    id: 'cold-exposure',
    pillar: 'mental',
    label: 'Cold exposure',
    description: 'Cold shower, plunge, or outdoor cold time. ≥ 2 minutes.',
    autoDetect: 'manual-tap',
    config: { minutes: 2 },
    configurable: true,
    hint: 'Manual tap',
    tags: ['resilience'],
  },
  {
    id: 'no-social-media-am',
    pillar: 'mental',
    label: 'No social media first hour',
    description: 'No social apps during the first hour after waking.',
    autoDetect: 'manual-tap',
    config: {},
    hint: 'Honor-system tap',
    tags: ['focus'],
  },
  {
    id: 'screen-cutoff',
    pillar: 'mental',
    label: 'No screens after 9 PM',
    description: 'Phone/TV off by your cutoff time.',
    autoDetect: 'screen-time-cutoff',
    config: { time: '21:00' },
    configurable: true,
    hint: 'Confirm tap at bedtime',
    tags: ['sleep-hygiene'],
  },

  // ── SPIRITUAL ────────────────────────────────────────────────────────
  // Doctrinally anchored — Army Spiritual Readiness explicitly includes
  // religious, philosophical, AND human values. Library reflects that.
  {
    id: 'scripture-reading',
    pillar: 'spiritual',
    label: 'Scripture / self-help book',
    description: 'Read scripture (Bible, Quran, Torah, etc.) or a self-help / philosophy book.',
    autoDetect: 'manual-tap',
    config: { goal: '' },          // free-text target, e.g. "10 pages of the Bible", "1 chapter"
    configurable: true,
    hint: 'Tap to mark complete',
    tags: ['core-discipline', 'growth'],
  },
  {
    id: 'prayer',
    pillar: 'spiritual',
    label: 'Prayer / contemplation',
    description: 'Personal prayer or contemplative practice.',
    autoDetect: 'manual-tap',
    config: { minutes: 0 },        // 0 = no minimum; set a target if desired
    configurable: true,
    hint: 'Manual tap',
    tags: ['religious'],
  },
  {
    id: 'gratitude',
    pillar: 'spiritual',
    label: 'Write 5 things you\'re grateful for',
    description: 'List 5 specific things from today you\'re grateful for.',
    autoDetect: 'manual-tap',
    config: { count: 5 },
    configurable: true,
    hint: 'Quick inline entry',
    tags: ['gratitude'],
  },
  {
    id: 'time-outdoors',
    pillar: 'spiritual',
    label: '10 min outdoors without phone',
    description: 'Stand or walk outside, phone away, for at least 10 minutes.',
    autoDetect: 'time-outdoors',
    config: { minutes: 10 },
    configurable: true,
    hint: 'Manual minute tracker',
    tags: ['nature', 'reset'],
  },
  {
    id: 'service',
    pillar: 'spiritual',
    label: 'Act of service',
    description: 'One deliberate act of service to someone — large or small.',
    autoDetect: 'manual-tap',
    config: { goal: '' },          // optional free-text intent, e.g. "Call a buddy"
    configurable: true,
    hint: 'Tap when done, optionally note what',
    tags: ['service'],
  },
  {
    id: 'values-check',
    pillar: 'spiritual',
    label: 'Values check-in',
    description: 'Review your personal values list. 1 minute of focused attention.',
    autoDetect: 'manual-tap',
    config: { minutes: 1 },
    configurable: true,
    hint: 'Manual tap',
    tags: ['identity'],
  },
];

// ============================================================================
// PRESETS — challenge templates
// ============================================================================

export interface Preset {
  id: string;
  name: string;
  /** Short hook line shown in picker. */
  tagline: string;
  /** Longer description for the detail view. */
  description: string;
  duration: number;
  /** Commitment IDs from the library. */
  commitments: { physical: string[]; mental: string[]; spiritual: string[] };
  failureMode: FailureMode;
  graceDays: number;
  /** Difficulty tier for UI ordering / colors. */
  difficulty: 'entry' | 'standard' | 'advanced' | 'extreme';
  /** Doctrinal anchor — shown in detail view to ground the preset. */
  doctrine?: string;
  /** Optional per-commitment config baked into the preset (e.g. weekly
   *  frequency, reduced minutes). Applied at challenge creation. Lets a
   *  preset ship "3×/week, 30-min sessions" without the user tuning it. */
  configOverrides?: Record<string, Record<string, unknown>>;
}

export const PRESETS: Preset[] = [
  // ── ENTRY tier (top of the picker — start-here options) ──
  {
    id: 'reset-7',
    name: '7-Day Reset',
    tagline: 'The easiest start. One small commitment per pillar.',
    description: 'A single week to break a slump. Walk 6k steps, write one journal entry, list one gratitude. Pillar-collapse + 2 grace days — designed to NOT fail. Momentum first; intensity later.',
    duration: 7,
    commitments: {
      physical: ['steps-10k'],
      mental: ['journal'],
      spiritual: ['gratitude'],
    },
    configOverrides: {
      'steps-10k': { steps: 6000 },
    },
    failureMode: 'pillar-collapse',
    graceDays: 2,
    difficulty: 'entry',
    doctrine: 'Behavior change starts with proving you can show up. Seven small wins beat one big plan you abandon. Step on the path; the rest follows.',
  },
  {
    id: 'sleep-first-30',
    name: 'Sleep First 30',
    tagline: 'Recovery is the engine. Fix sleep before training.',
    description: '30 days protecting sleep. Wake at a consistent time, 7+ hours, no screens after 9 PM. Light step floor keeps the body in the game without competing for attention.',
    duration: 30,
    commitments: {
      physical: ['sleep-7hr', 'steps-10k'],
      mental: ['wake-by-time', 'screen-cutoff'],
      spiritual: ['gratitude'],
    },
    configOverrides: {
      'steps-10k': { steps: 7000 },
    },
    failureMode: 'pillar-collapse',
    graceDays: 4,
    difficulty: 'entry',
    doctrine: 'Sleep is the cheapest, highest-leverage performance lever (Walker, "Why We Sleep"). Chronic <7 hours cuts strength, HRV, glucose tolerance, and immune function. Fix sleep first; everything downstream gets easier.',
  },
  {
    id: 'move-daily-21',
    name: 'Move Daily 21',
    tagline: 'Three weeks of just showing up.',
    description: 'Workout three days a week (20 min min, no PR pressure), 8k steps every day, 7+ hours of sleep. Designed to rebuild the training habit without the daily-gym grind.',
    duration: 21,
    commitments: {
      physical: ['workout-45min', 'steps-10k', 'sleep-7hr'],
      mental: ['journal'],
      spiritual: ['gratitude'],
    },
    configOverrides: {
      'workout-45min': { weeklyFrequency: 3, minutes: 20 },
      'steps-10k': { steps: 8000 },
    },
    failureMode: 'pillar-collapse',
    graceDays: 3,
    difficulty: 'entry',
    doctrine: '21 days is a habit-priming window — long enough to feel the rhythm, short enough not to flinch from. Frequency beats duration when rebuilding consistency.',
  },
  {
    id: 'faith-foundation-21',
    name: 'Faith Foundation 21',
    tagline: 'Spiritual reps. Prayer, scripture, gratitude — daily.',
    description: 'A 21-day spiritual practice block. Prayer/contemplation, scripture or spiritual reading, daily gratitude. Light physical floor keeps the body moving without competing for attention.',
    duration: 21,
    commitments: {
      physical: ['steps-10k'],
      mental: ['journal'],
      spiritual: ['scripture-reading', 'prayer', 'gratitude'],
    },
    configOverrides: {
      'steps-10k': { steps: 6000 },
    },
    failureMode: 'pillar-collapse',
    graceDays: 3,
    difficulty: 'entry',
    doctrine: 'The spiritual pillar is the one most often skipped because it has the least immediate feedback. A dedicated block forces attention back to the "why" beneath the "what."',
  },
  // ── STANDARD tier ──
  {
    id: 'calm-30',
    name: 'Calm 30',
    tagline: 'Lower the noise. Less booze, more breath.',
    description: '30 days of no alcohol + daily meditation + daily journal. Lighter than Sober 30 — meditation is the active habit, not abstinence as the only goal. For the alcohol-curious or anyone trying to lower baseline stress.',
    duration: 30,
    commitments: {
      physical: ['sleep-7hr'],
      mental: ['no-alcohol', 'meditate-10min', 'journal'],
      spiritual: ['gratitude'],
    },
    failureMode: 'pillar-collapse',
    graceDays: 3,
    difficulty: 'standard',
    doctrine: 'Mindfulness as a replacement habit. Removing alcohol without adding something leaves the original need unmet. Meditation + journaling become the floor that catches what alcohol used to mask.',
  },
  // ── Legacy presets (advanced + extreme tier) ──
  {
    id: '75-hard-classic',
    name: '75 Hard (Classic)',
    tagline: 'Frisella\'s original. Brutal.',
    description: 'Two 45-min workouts daily (one outdoors), under calorie goal, 1 gallon of water, 10 pages of non-fiction, daily scripture/self-help reading. Miss anything → restart from Day 1.',
    duration: 75,
    commitments: {
      physical: ['workout-2x45min', 'calorie-under-goal', 'water-gallon'],
      mental: ['reading-10pages', 'no-alcohol'],
      spiritual: ['scripture-reading'],
    },
    failureMode: 'strict',
    graceDays: 0,
    difficulty: 'extreme',
    doctrine: 'Originated by Andy Frisella as a mental toughness program. Strict by design — the brutality is the feature.',
  },
  {
    id: '75-hard-custom',
    name: '75 Hard (OPMODE Custom)',
    tagline: 'Wake, work out, eat clean, read, dry.',
    description: 'Wake by 6:30 AM, workout 45+ min (Sunday yoga), under calorie goal, no alcohol, daily scripture/self-help reading. Customizable.',
    duration: 75,
    commitments: {
      physical: ['workout-45min', 'yoga-sunday', 'calorie-under-goal'],
      mental: ['wake-by-time', 'no-alcohol'],
      spiritual: ['scripture-reading'],
    },
    failureMode: 'strict',
    graceDays: 0,
    difficulty: 'advanced',
    doctrine: 'Adapted from 75 Hard for a tactical-discipline focus. Sunday yoga preserves recovery without breaking the streak.',
  },
  {
    id: 'operator-90',
    name: 'Operator 90',
    tagline: 'SOF-style. 90-day arc, pillar-balanced.',
    description: 'Three months of high-floor discipline. Workout, sleep, steps, no alcohol, wake on time, daily scripture/self-help + gratitude practice. Pillar-collapse mode — pick multiple per pillar for buffer.',
    duration: 90,
    commitments: {
      physical: ['workout-45min', 'sleep-7hr', 'steps-10k', 'calorie-under-goal'],
      mental: ['wake-by-time', 'no-alcohol', 'journal'],
      spiritual: ['scripture-reading', 'gratitude'],
    },
    failureMode: 'pillar-collapse',
    graceDays: 3,
    difficulty: 'advanced',
    doctrine: 'Inspired by SOCOM POTFF / THOR3 frameworks. 90 days is long enough to encode habits; pillar-collapse mode favors sustainability over single-item brittleness.',
  },
  {
    id: 'reset-45',
    name: 'Reset 45',
    tagline: 'Entry-level. Post-injury or post-break.',
    description: 'A gentler 45-day baseline. Hit a workout most days, get sleep, stay sober, read something. Designed for coming back from injury, a layoff, or just hard times.',
    duration: 45,
    commitments: {
      physical: ['workout-45min', 'sleep-7hr'],
      mental: ['no-alcohol'],
      spiritual: ['scripture-reading'],
    },
    failureMode: 'pillar-collapse',
    graceDays: 5,
    difficulty: 'entry',
    doctrine: 'Reentry protocol. Reduced volume + grace days acknowledge that consistency beats intensity when rebuilding.',
  },
  {
    id: 'sober-30',
    name: 'Sober 30',
    tagline: 'Alcohol-focused. 30 days dry + the basics.',
    description: '30 days of no alcohol, daily workout, journal entry. Designed for sober-curious or a deliberate reset.',
    duration: 30,
    commitments: {
      physical: ['workout-45min'],
      mental: ['no-alcohol', 'journal'],
      spiritual: ['gratitude'],
    },
    failureMode: 'strict',
    graceDays: 0,
    difficulty: 'standard',
    doctrine: 'Aligned with Dry January / Sober October frameworks. Strict on the alcohol axis — the whole point.',
  },
  {
    id: 'recondition-30',
    name: 'Reconditioning 30',
    tagline: 'Less gym, more base. Prep for a harder challenge.',
    description: 'A 30-day on-ramp. Build the daily habits — sleep, wake time, steps, journaling, gratitude — while keeping training LIGHT: 3 short sessions a week, not daily gym grind. Designed to lay the foundation before you take on a 75 Hard or Operator 90.',
    duration: 30,
    commitments: {
      physical: ['workout-45min', 'sleep-7hr', 'steps-10k'],
      mental: ['wake-by-time', 'journal'],
      spiritual: ['gratitude', 'scripture-reading'],
    },
    // Training stays low-pressure: 3 short sessions/week (weekly target,
    // optional on off-days) instead of a daily 45-min requirement. The
    // daily habits (sleep, wake, steps, journal, gratitude, reading)
    // carry the streak. Reading goal kept gentle.
    configOverrides: {
      'workout-45min': { weeklyFrequency: 3, minutes: 30 },
      'steps-10k': { steps: 7000 },
      'scripture-reading': { goal: 'A few pages — keep it sustainable' },
    },
    failureMode: 'pillar-collapse',
    graceDays: 5,
    difficulty: 'entry',
    doctrine: 'Reconditioning protocol. The fastest way to fail a hard challenge is to start unconditioned. This block builds the habit floor — sleep, circadian anchor, daily movement, reflection — at low training stress, so the harder challenge that follows lands on a foundation instead of bare ground.',
  },
];

/**
 * Synthetic "blank canvas" preset. Selecting this opens the builder
 * with no commitments pre-filled — the user picks from COMMITMENT_LIBRARY
 * across the three pillars and sets their own duration. Kept out of
 * the PRESETS array so it doesn't render as a standard tinted card;
 * the picker surfaces it as a dedicated "Build my own" affordance.
 *
 * Defaults are intentionally forgiving (pillar-collapse + 3 grace days)
 * because users who go custom are usually optimizing for sustainability
 * over Frisella-style brutality.
 */
// ============================================================================
// PRESET METADATA — focus tags + "best for" copy
// ----------------------------------------------------------------------------
// Stored beside (not inside) Preset so adding/removing tags doesn't churn
// the array. Drives filter chips in the picker and the recommender's
// matching score.
// ============================================================================

export type Focus = 'foundation' | 'reentry' | 'sleep' | 'sobriety' | 'faith' | 'discipline' | 'mental';

export const FOCUS_LABEL: Record<Focus, string> = {
  foundation: 'Foundation',
  reentry: 'Re-entry',
  sleep: 'Sleep',
  sobriety: 'Sobriety',
  faith: 'Faith',
  discipline: 'Discipline',
  mental: 'Mental',
};

export interface PresetMeta {
  focus: Focus[];
  bestFor: string;
  /**
   * Suggested next challenges after completing this one, in priority order.
   * Powers the "What's next?" panel on the completed view so the user
   * keeps momentum instead of dropping out of the loop.
   */
  nextStep?: string[];
}

export const PRESET_META: Record<string, PresetMeta> = {
  'reset-7':              { focus: ['foundation', 'reentry'], bestFor: 'First-time start or breaking a slump', nextStep: ['move-daily-21', 'sleep-first-30', 'recondition-30'] },
  'sleep-first-30':       { focus: ['sleep'],                  bestFor: 'Training being eaten by bad sleep',     nextStep: ['operator-90', 'move-daily-21'] },
  'move-daily-21':        { focus: ['foundation', 'reentry'],  bestFor: 'Rebuilding the training habit',          nextStep: ['recondition-30', 'operator-90'] },
  'faith-foundation-21':  { focus: ['faith'],                  bestFor: 'Deepening spiritual practice',           nextStep: ['operator-90', 'sober-30'] },
  'calm-30':              { focus: ['sobriety', 'mental'],     bestFor: 'Alcohol-curious + lowering stress',      nextStep: ['sober-30', 'operator-90'] },
  '75-hard-classic':      { focus: ['discipline'],             bestFor: 'You want the brutal one',                nextStep: ['operator-90'] },
  '75-hard-custom':       { focus: ['discipline'],             bestFor: 'Tactical discipline with built-in recovery', nextStep: ['75-hard-classic', 'operator-90'] },
  'operator-90':          { focus: ['discipline', 'reentry'],  bestFor: '3-month tactical block, pillar-balanced', nextStep: ['75-hard-custom', '75-hard-classic'] },
  'reset-45':             { focus: ['reentry'],                bestFor: 'Coming back from a long break or injury', nextStep: ['operator-90', '75-hard-custom'] },
  'sober-30':             { focus: ['sobriety'],               bestFor: 'Dry month, full stop',                   nextStep: ['operator-90', '75-hard-custom'] },
  'recondition-30':       { focus: ['foundation', 'reentry'],  bestFor: 'On-ramp before a hard challenge',        nextStep: ['operator-90', 'move-daily-21'] },
};

export function getPresetMeta(id: string): PresetMeta {
  return PRESET_META[id] ?? { focus: [], bestFor: '' };
}

// ============================================================================
// DYNAMIC LABELS — reflect the user's tunings in the displayed commitment text
// ----------------------------------------------------------------------------
// The COMMITMENT_LIBRARY labels are static ("Workout 45+ min", "Wake by 6:30
// AM"). When a user tunes a commitment via the config sheet (e.g. drops
// workout minutes to 30, moves wake to 5:30), the row used to keep showing
// the original static text — so the change felt invisible until launch.
// `displayLabel` rebuilds the text from the effective config so the row
// updates as soon as the user saves a tune.
// ============================================================================

function formatTime12h(time24: string): string {
  const [hStr, mStr] = (time24 || '').split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (isNaN(h) || isNaN(m)) return time24;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

export function displayLabel(commitment: Commitment, cfg: Record<string, unknown>): string {
  switch (commitment.id) {
    case 'workout-45min':
      return typeof cfg.minutes === 'number' && cfg.minutes > 0
        ? `Workout ${cfg.minutes}+ min`
        : commitment.label;
    case 'workout-2x45min':
      return typeof cfg.minutes === 'number' && cfg.minutes > 0
        ? `2× ${cfg.minutes}-min workouts (one outdoors)`
        : commitment.label;
    case 'wake-by-time':
      return typeof cfg.time === 'string' && cfg.time.length > 0
        ? `Wake by ${formatTime12h(cfg.time)}`
        : commitment.label;
    case 'sleep-7hr': {
      // config carries minutes (default 420 = 7h); display in hours
      const mins = typeof cfg.minutes === 'number' ? cfg.minutes : null;
      if (mins && mins > 0) {
        const hrs = mins / 60;
        const hrsStr = hrs % 1 === 0 ? `${hrs}` : hrs.toFixed(1);
        return `${hrsStr}+ hours sleep`;
      }
      return commitment.label;
    }
    case 'steps-10k':
      return typeof cfg.steps === 'number' && cfg.steps > 0
        ? `${cfg.steps.toLocaleString()} steps`
        : commitment.label;
    case 'water-gallon':
      return typeof cfg.ounces === 'number' && cfg.ounces > 0
        ? `Drink ${cfg.ounces} oz water`
        : commitment.label;
    case 'reading-10pages':
      return typeof cfg.pages === 'number' && cfg.pages > 0
        ? `Read ${cfg.pages} pages of non-fiction`
        : commitment.label;
    case 'meditate-10min':
      return typeof cfg.minutes === 'number' && cfg.minutes > 0
        ? `${cfg.minutes} min meditation`
        : commitment.label;
    case 'screen-cutoff':
      return typeof cfg.time === 'string' && cfg.time.length > 0
        ? `No screens after ${formatTime12h(cfg.time)}`
        : commitment.label;
    case 'gratitude':
      return typeof cfg.count === 'number' && cfg.count > 0
        ? `Write ${cfg.count} thing${cfg.count === 1 ? '' : 's'} you're grateful for`
        : commitment.label;
    case 'time-outdoors':
      return typeof cfg.minutes === 'number' && cfg.minutes > 0
        ? `${cfg.minutes} min outdoors without phone`
        : commitment.label;
    case 'prayer':
      return typeof cfg.minutes === 'number' && cfg.minutes > 0
        ? `${cfg.minutes} min prayer / contemplation`
        : commitment.label;
    case 'scripture-reading':
      return typeof cfg.goal === 'string' && cfg.goal.trim().length > 0
        ? cfg.goal.trim()
        : commitment.label;
    default:
      return commitment.label;
  }
}

/** Convenience: resolves the effective config from the three layers
 *  (commitment defaults + preset override + user override) and returns
 *  the display label in one call. Pass `presetOverride` and `userOverride`
 *  from the BUILDER's `preset.configOverrides?.[id]` + `customConfigs[id]`. */
export function effectiveDisplayLabel(
  commitment: Commitment,
  presetOverride?: Record<string, unknown>,
  userOverride?: Record<string, unknown>,
): string {
  const cfg = { ...(commitment.config ?? {}), ...(presetOverride ?? {}), ...(userOverride ?? {}) };
  return displayLabel(commitment, cfg);
}

export const CUSTOM_PRESET: Preset = {
  id: 'custom',
  name: 'Build my own',
  tagline: 'Pick the commitments that fit your life.',
  description: 'Choose any combination of commitments across the three pillars, set your own duration. You decide what your discipline looks like.',
  duration: 75,
  commitments: { physical: [], mental: [], spiritual: [] },
  failureMode: 'pillar-collapse',
  graceDays: 3,
  difficulty: 'standard',
  doctrine: 'No doctrinal anchor — you\'re writing the doctrine.',
};

// ============================================================================
// HELPERS
// ============================================================================

/** Look up a commitment by id. Returns null if unknown. */
export function getCommitment(id: string): Commitment | null {
  return COMMITMENT_LIBRARY.find(c => c.id === id) ?? null;
}

/** Look up a preset by id. Includes the synthetic CUSTOM_PRESET so the
 *  builder can route from a saved challenge back to the right launcher. */
export function getPreset(id: string): Preset | null {
  if (id === CUSTOM_PRESET.id) return CUSTOM_PRESET;
  return PRESETS.find(p => p.id === id) ?? null;
}

/** All commitments in a challenge, flat. Order: physical → mental → spiritual. */
export function challengeCommitments(challenge: Challenge): Commitment[] {
  const ids = [
    ...challenge.commitments.physical,
    ...challenge.commitments.mental,
    ...challenge.commitments.spiritual,
  ];
  return ids.map(getCommitment).filter((c): c is Commitment => c !== null);
}

/**
 * Weekly-target frequency for a commitment, or null when it's a normal
 * daily commitment. A value 1-6 means "complete this N days this week"
 * — the commitment is OPTIONAL on any given day and does NOT count
 * toward daily pass/fail. 7 would just be daily, so we cap at 6.
 */
export function getWeeklyFrequency(
  commitmentId: string,
  challenge: Pick<Challenge, 'configOverrides'>
): number | null {
  const wf = challenge.configOverrides?.[commitmentId]?.weeklyFrequency;
  if (typeof wf === 'number' && wf >= 1 && wf <= 6) return wf;
  return null;
}

export function isWeeklyCommitment(
  commitmentId: string,
  challenge: Pick<Challenge, 'configOverrides'>
): boolean {
  return getWeeklyFrequency(commitmentId, challenge) !== null;
}

/**
 * Is this commitment REQUIRED on `date`?
 *
 * Two ways a commitment can be configured but not required today:
 *
 *   1. Weekly target (`configOverrides[id].weeklyFrequency` of 1-6) — the
 *      user owes N days this week, not this specific day.
 *   2. Day-of-week gate (`dayOfWeek` in the effective config, 0 = Sunday).
 *      `yoga-sunday` ships with `dayOfWeek: 0`, and its detector already
 *      abstains (returns null) on the other six days — so before this
 *      helper existed it was configured, never satisfiable Mon-Sat, and
 *      still counted in the strict `total`. That made the shipped
 *      `75-hard-custom` preset (strict, 0 grace days, includes
 *      `yoga-sunday`) IMPOSSIBLE to pass six days out of seven.
 *
 * Anything else — including an id the library no longer knows — is
 * required. An unknown id keeps the pre-existing behaviour; a missing
 * library entry must not quietly make a day easier to pass.
 *
 * Accepts a Commitment or a bare id so both the pillar tallies (which
 * work off the raw id lists) and detector code can call it.
 */
export function isDailyRequired(
  commitment: Commitment | string,
  challenge: Pick<Challenge, 'configOverrides'>,
  date: Date = new Date(),
): boolean {
  const id = typeof commitment === 'string' ? commitment : commitment.id;
  if (isWeeklyCommitment(id, challenge)) return false;
  const resolved = typeof commitment === 'string' ? getCommitment(id) : commitment;
  if (!resolved) return true;
  const cfg = { ...resolved.config, ...(challenge.configOverrides?.[id] ?? {}) };
  const dow = cfg.dayOfWeek;
  if (typeof dow === 'number') return date.getDay() === dow;
  return true;
}

/** Compute a day's outcome from a DailyStatus + challenge config.
 *
 * Only commitments REQUIRED on `date` (see isDailyRequired) count toward
 * pass/fail — weekly-target items have their own week-level tracking, and
 * a day-gated item is nobody's miss on a day it doesn't apply to. */
export function computeDayOutcome(
  status: Pick<DailyStatus, 'pillarCounts' | 'completed'>,
  challenge: Pick<Challenge, 'commitments' | 'failureMode' | 'configOverrides'>,
  /** The day being evaluated. Defaults to today for legacy callers, but
   *  rollover / catch-up MUST pass the date it is actually evaluating —
   *  otherwise a Sunday-gated item is judged against today's weekday. */
  date: Date = new Date(),
): 'passed' | 'failed' | 'pending' {
  // Filter each pillar's commitments down to the ones required on `date`.
  const dailyIds = (ids: string[]) => ids.filter(id => isDailyRequired(id, challenge, date));
  const physicalDaily = dailyIds(challenge.commitments.physical);
  const mentalDaily = dailyIds(challenge.commitments.mental);
  const spiritualDaily = dailyIds(challenge.commitments.spiritual);

  const total = physicalDaily.length + mentalDaily.length + spiritualDaily.length;
  const doneDaily = (ids: string[]) => ids.filter(id => !!status.completed[id]).length;
  const done = doneDaily(physicalDaily) + doneDaily(mentalDaily) + doneDaily(spiritualDaily);

  if (challenge.failureMode === 'strict') {
    if (total === 0) return 'passed'; // all-weekly day (rare) — nothing daily to fail
    if (done === total) return 'passed';
    return 'pending'; // until midnight rollover, treat as still possible
  }

  // pillar-collapse: each pillar with any DAILY commitments needs ≥1 daily done
  const physicalOk = physicalDaily.length === 0 || doneDaily(physicalDaily) > 0;
  const mentalOk = mentalDaily.length === 0 || doneDaily(mentalDaily) > 0;
  const spiritualOk = spiritualDaily.length === 0 || doneDaily(spiritualDaily) > 0;
  if (physicalOk && mentalOk && spiritualOk) return 'passed';
  return 'pending';
}
